import { Typography } from "@mui/material";
import * as Yup from "yup";

export const initialValues = (data) => {
  const genders = {
    male: 1,
    female: 2,
    any: 3,
  };

  const mapOption = (option) => ({
    label: option?.name,
    value: option?.id,
  });

  const allCategories = data?.categories || [];
  const parentCategories = allCategories
    .filter((option) => !option?.parent_id)
    .map(mapOption);
  const childCategories = [
    ...allCategories.filter((option) => option?.parent_id).map(mapOption),
    ...(data?.subcategories || []).map(mapOption),
  ].filter(
    (option, index, list) =>
      list.findIndex((item) => item.value === option.value) === index,
  );

  // Ensure parents referenced by subcategories appear in the category field
  const parentIds = new Set(parentCategories.map((item) => item.value));
  allCategories
    .filter((option) => option?.parent_id)
    .forEach((option) => {
      if (!parentIds.has(option.parent_id)) {
        parentCategories.push({
          label: option?.parent_name || option?.parent?.name || String(option.parent_id),
          value: option.parent_id,
        });
        parentIds.add(option.parent_id);
      }
    });

  return {
    ...data,
    ...data?.localized_data,
    thumbnail: data?.thumbnail,
    images: data?.media?.map((img) => img.url),
    store_id: data?.store?.id,
    brand_id: data?.brand?.id ?? data?.brand_id ?? null,
    option_types: data?.option_types?.map((option) => {
      return {
        label: option?.name,
        value: option?.id,
      };
    }),
    tag_ids: data?.tags?.map((option) => {
      return {
        label: option?.name,
        value: option?.id,
      };
    }),
    occasion_types: data?.occasion_types_details?.map((option) => {
      return {
        label: option?.name,
        value: option?.id,
      };
    }),
    categories: parentCategories,
    subcategories: childCategories,
    target_gender: genders[data?.target_gender],
    recommended_products:
      data?.recommended_products?.map((c) => ({
        label: c.name,
        value: c.id,
      })) || [],
    recommended_categories:
      data?.recommended_categories?.map((c) => ({
        label: c.name,
        value: c.id,
      })) || [],
    display_priority: data?.display_priority || "",
    featured_product_priority: data?.featured_product_priority || "",
    featured_store_priority: data?.featured_store_priority || "",
    featured_category_priority: data?.featured_category_priority || "",
    featured_occasion_priority: data?.featured_occasion_priority || "",
    is_featured: data?.is_featured || false,
    featured_in_occasions: data?.featured_in_occasions || false,
    featured_in_categories: data?.featured_in_categories || false,
    featured_in_store: data?.featured_in_store || false,
  };
};

export const structure = (
  options,
  isStore,
  t,
  formikValues,
  isEventService = false,
) => {
  return [
    {
      head: "",
      width: "100%",
      list: [
        {
          name: `thumbnail`,
          kind: "upload",
          type: "image",
          label: `${t("products.label_thumbnail")}*`,
          placeholder: "test",
          text: t("products.text_upload_desc"),
          width: "35%",
          id: "10",
        },
        { width: "47%" },
        {
          name: `images`,
          kind: "multiUpload",
          type: "text",
          label: `${t("products.label_images")}*`,
          width: "100%",
          enableVideo: true,
        },
        // { width: "20%" },
      ],
    },
    {
      head: (
        <Typography fontSize="32px" fontWeight="400">
          {isEventService
            ? t("events_services.label_details")
            : t("products.label_details")}
        </Typography>
      ),
      width: "100%",
      list: [
        !isStore &&
          !isEventService && {
          name: `store_id`,
          kind: "multiSelect",
          type: "text",
          label: `${t("products.col_vendor")}*`,
          width: "100%",
          placeholder: t("products.placeholder_choose_vendor"),
          height: "250px",
          isMulti: false,
          ...options?.vendors,
        },
        !isEventService && {
          name: `brand_id`,
          kind: "multiSelect",
          type: "text",
          label: t("products.label_brand"),
          width: "47%",
          placeholder: t("products.placeholder_choose_brand"),
          height: "250px",
          isMulti: false,
          ...options?.brands,
        },
        {
          name: `en.name`,
          kind: "input",
          type: "text",
          label: `${
            isEventService
              ? t("events_services.placeholder_name_en")
              : t("products.placeholder_name_en")
          }*`,
          width: "47%",
          placeholder: isEventService
            ? t("events_services.placeholder_name_en")
            : t("products.placeholder_name_en"),
        },
        {
          name: `ar.name`,
          kind: "input",
          type: "text",
          label: isEventService
            ? t("events_services.placeholder_name_ar")
            : t("products.placeholder_name_ar"),
          width: "47%",
          placeholder: isEventService
            ? t("events_services.placeholder_name_ar")
            : t("products.placeholder_name_ar"),
        },
        {
          name: `price`,
          kind: "input",
          type: "number",
          label: `${t("products.col_price")}*`,
          width: "47%",
          placeholder: t("products.placeholder_price"),
          EndAdornment: (
            <p style={{ fontSize: "18px", color: "#1A1A1A" }}>SAR</p>
          ),
        },
        {
          name: `price_after_discount`,
          kind: "input",
          type: "number",
          label: t("products.col_price_after_discount"),
          width: "47%",
          placeholder: t("products.placeholder_price"),
          EndAdornment: (
            <p style={{ fontSize: "18px", color: "#1A1A1A" }}>SAR</p>
          ),
        },
        !isEventService && {
          name: `preparation_time`,
          kind: "input",
          type: "number",
          label: t("products.label_preparation_time"),
          width: "47%",
          placeholder: t("products.placeholder_preparation_time"),
          hint: t("products.hint_preparation_time"),
          EndAdornment: (
            <p style={{ fontSize: "18px", color: "#1A1A1A" }}>
              {t("products.days")}
            </p>
          ),
        },
        !isEventService && {
          name: `occasion_types`, //arrau of occasion types ids
          kind: "multiSelect",
          type: "text",
          label: t("products.label_occasions"),
          width: "47%",
          ...options?.occassions,
          placeholder: t("products.placeholder_choose_occasion"),
          height: "250px",
        },
        !isEventService && {
          name: `categories`, //array
          kind: "multiSelect",
          type: "text",
          label: t("products.label_category"),
          width: "47%",
          placeholder: t("products.placeholder_choose_category"),
          options: options?.categories?.options,
          onOpen: options?.categories?.onOpen,
          isLoading: options?.categories?.isLoading,
          getList: options?.categories?.getList,
          applySearchFromRequest: options?.categories?.applySearchFromRequest,
          count: options?.categories?.count,
          changeAction: (value, formik) => {
            formik.setValues({
              ...formik.values,
              categories: value,
              subcategories: [],
            });
            options?.onCategoriesChange?.(value);
          },
        },
        !isEventService && {
          name: `subcategories`,
          kind: "multiSelect",
          type: "text",
          label: t("products.label_subcategory"),
          width: "47%",
          placeholder: t("products.placeholder_choose_subcategory"),
          options: options?.subcategories?.options,
          onOpen: options?.subcategories?.onOpen,
          isLoading: options?.subcategories?.isLoading,
          getList: options?.subcategories?.getList,
          applySearchFromRequest:
            options?.subcategories?.applySearchFromRequest,
          count: options?.subcategories?.count,
          isDisabled: !(formikValues?.categories?.length > 0),
        },
        !isEventService && {
          name: `tag_ids`, //array of tag ids
          kind: "multiSelect",
          type: "text",
          label: t("products.label_tags"),
          width: "47%",
          ...options?.tags,
          placeholder: t("products.placeholder_choose_tags"),
          height: "250px",
        },
        {
          name: `option_types`,
          kind: "multiSelect",
          type: "text",
          label: `${t("products.label_select_option_type")}*`,
          placeholder: t("products.placeholder_choose_option_type"),
          width: "47%",
          ...options?.optionTypes,
        },
        {
          name: `en.description`,
          kind: "textArea",
          type: "text",
          label: t("products.label_description_en"),
          width: "47%",
          placeholder: t("products.placeholder_description_en"),
          colSpan: 6,
        },
        {
          name: `ar.description`,
          kind: "textArea",
          type: "text",
          label: t("products.label_description_ar"),
          width: "47%",
          placeholder: t("products.placeholder_description_ar"),
          colSpan: 6,
        },
        !isEventService && {
          name: `target_age`,
          kind: "select",
          type: "text",
          label: t("products.label_select_age"),
          width: "100%",
          placeholder: t("products.label_select_age"),
          options: [
            { id: "babies", name: t("products.age_babies") },
            { id: "teenagers", name: t("products.age_teenagers") },
            { id: "adults", name: t("products.age_adults") },
            { id: "any", name: t("products.age_any") },
          ],
        },
        !isEventService && {
          name: `recommended_products`,
          kind: "multiSelect",
          type: "text",
          label: t("products.label_recommended_products"),
          width: "100%",
          placeholder: t("products.placeholder_choose_products"),
          ...options?.recommendations,
        },
        !isEventService && {
          name: `recommended_categories`,
          kind: "multiSelect",
          type: "text",
          label: t("products.label_recommended_categories"),
          width: "100%",
          placeholder: t("products.placeholder_choose_categories"),
          ...options?.recommendedCategories,
        },
        !isEventService && {
          name: `target_gender`,
          kind: "radioBox",
          type: "text",
          label: t("products.label_gender"),
          width: "47%",
          placeholder: t("products.placeholder_price"),
          options: [
            { value: "1", label: t("products.gender_male") },
            { value: "2", label: t("products.gender_female") },
            { value: "3", label: t("products.gender_any") },
          ],
        },
        {
          name: `is_visible`,
          kind: "checkbox",
          type: "text",
          label: t("products.label_active"),
          width: isEventService ? "100%" : "20%",
        },
        !isEventService && {
          name: `is_wrappable`,
          kind: "checkbox",
          type: "text",
          label: t("products.label_is_wrappable"),
          width: "20%",
        },
        !isEventService && {
          name: `display_priority`,
          kind: "input",
          type: "number",
          label: t("products.display_priority"),
          width: "calc(60% - 24px)",
          // disabled: isStore,
        },
        !isEventService && {
          name: `is_featured`,
          kind: "checkbox",
          type: "text",
          label: t("products.is_featured"),
          width: "calc(50% - 12px)",
          disabled: isStore,
        },
        !isEventService && {
          name: `featured_product_priority`,
          kind: "input",
          type: "number",
          label: t("products.featured_product_priority"),
          width: "calc(50% - 12px)",
          disabled: isStore,
        },
        !isEventService && {
          name: `featured_in_store`,
          kind: "checkbox",
          type: "text",
          label: t("products.featured_in_store"),
          width: "calc(50% - 12px)",
          disabled: isStore,
        },
        !isEventService && {
          name: `featured_store_priority`,
          kind: "input",
          type: "number",
          label: t("products.featured_store_priority"),
          width: "calc(50% - 12px)",
          disabled: isStore,
        },
        !isEventService && {
          name: `featured_in_categories`,
          kind: "checkbox",
          type: "text",
          label: t("products.featured_in_categories"),
          width: "calc(50% - 12px)",
          disabled: isStore,
        },
        !isEventService && {
          name: `featured_category_priority`,
          kind: "input",
          type: "number",
          label: t("products.featured_category_priority"),
          width: "calc(50% - 12px)",
          disabled: isStore,
        },
        !isEventService && {
          name: `featured_in_occasions`,
          kind: "checkbox",
          type: "text",
          label: t("products.featured_in_occasions"),
          width: "calc(50% - 12px)",
          disabled: isStore,
        },
        !isEventService && {
          name: `featured_occasion_priority`,
          kind: "input",
          type: "number",
          label: t("products.featured_occasion_priority"),
          width: "calc(50% - 12px)",
          disabled: isStore,
        },
      ],
    },
  ];
};

export const validationSchema = (t, isEventService = false) =>
  Yup.object({
    thumbnail: Yup.string().required(t("vendors.error_required")),
    images: Yup.array().required(t("vendors.error_required")),
    store_id: isEventService
      ? Yup.mixed().nullable()
      : Yup.string().required(t("vendors.error_required")),
    brand_id: Yup.mixed().nullable(),
    is_visible: Yup.boolean(),
    is_wrappable: Yup.boolean(),
    display_priority: Yup.number()
      .transform((value, originalValue) =>
        originalValue === "" || originalValue === null || originalValue === undefined
          ? null
          : value,
      )
      .nullable()
      .typeError("Must be a number"),
    featured_product_priority: Yup.number()
      .transform((value, originalValue) =>
        originalValue === "" || originalValue === null || originalValue === undefined
          ? null
          : value,
      )
      .nullable()
      .typeError("Must be a number"),
    featured_store_priority: Yup.number()
      .transform((value, originalValue) =>
        originalValue === "" || originalValue === null || originalValue === undefined
          ? null
          : value,
      )
      .nullable()
      .typeError("Must be a number"),
    featured_category_priority: Yup.number()
      .transform((value, originalValue) =>
        originalValue === "" || originalValue === null || originalValue === undefined
          ? null
          : value,
      )
      .nullable()
      .typeError("Must be a number"),
    featured_occasion_priority: Yup.number()
      .transform((value, originalValue) =>
        originalValue === "" || originalValue === null || originalValue === undefined
          ? null
          : value,
      )
      .nullable()
      .typeError("Must be a number"),
    is_featured: Yup.boolean(),
    featured_in_occasions: Yup.boolean(),
    featured_in_categories: Yup.boolean(),
    featured_in_store: Yup.boolean(),
    en: Yup.object().shape({
      name: Yup.string().trim().required(t("vendors.error_required")),
    }),
    // ar: Yup.object().shape({
    //   name: Yup.string().trim(),
    //   description: Yup.string().trim(),
    // }),
    price: Yup.number(t("vendors.error_invalid_price")).required(
      t("vendors.error_required"),
    ),
    price_after_discount: Yup.number()
      .typeError(t("vendors.error_invalid_price"))
      .nullable()
      .test(
        "is-greater",
        t("products.error_price_after_discount_min"),
        function (value) {
          const { price } = this.parent;
          if (value == null) {
            return true; // Allow empty price_after_discount
          }
          return value <= price;
        },
      ),
    preparation_time: Yup.number()
      .typeError(t("products.error_invalid_preparation_time"))
      .min(0, t("products.error_invalid_preparation_time"))
      .nullable(),

    option_types: Yup.array()
      .min(1, t("products.error_option_types_required"))
      .required(t("vendors.error_required")),
    target_age: isEventService
      ? Yup.mixed().nullable()
      : Yup.string().min(1, t("products.error_select_age")),
    target_gender: Yup.string(),
  });
